import React, { useCallback, useEffect, useState } from 'react'
import { AuthPageLayout, LoginForm, TeiaLoginForm } from 'iebt-zing-storybook'
import { EspacoTeiaAuthPageLayout } from 'components/branding/EspacoTeiaAuthPageLayout'
import { useSmartNavigateLegacy } from 'components/SmartLink'
import { trans } from 'sharedKernel/i18n'
import { useDispatch, useSelector } from 'react-redux'
import { setAuth, setPermissions } from 'sharedKernel/auth'
import { LoginFields } from 'iebt-zing-storybook/dist/forms/LoginForm'
import { api, postProvider, PostFormat } from 'sharedKernel/api'
import { getStartRoute } from 'sharedKernel/handlers'
import { showToast } from 'components/toast'
import _ from 'lodash'
import { TReducers, store } from 'store'
import { updateProfileImageLink } from 'domain/feature/hub/user-config/PersonalConfigForm/store/profileImage'
import { caixaUser } from 'sharedKernel/user/CaixaUser'
import { cookies } from 'sharedKernel/cookies/behavior'
import { LANDING_PAGE_URL } from 'sharedKernel/constants'

interface ExtendedLoginFields extends LoginFields {
  Authorization?: string;
  twoFactorCode?: string;
}

export const IndexPage = (): JSX.Element => {
  const navigate = useSmartNavigateLegacy()
  const dispatch = useDispatch()
  const { mutate } = postProvider()
  const userAuth = useSelector((state: TReducers) => state.auth)
  const [isLoading, setIsLoading] = useState(false)
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false)

  useEffect(() => {
    redirectIfUserIsLogged()
  }, [])

  const redirectIfUserIsLogged = (): void => {
    if (userAuth.isAuthenticated) {
      const permissions: string[] | undefined = userAuth?.permissions?.features.map((feature) => feature.code)
      redirectAfterLogin(permissions || [])
    }
  }

  const redirectAfterLogin = (permissions: string[]) => {
    const challengeId = localStorage.getItem("redirectChallengeId")
    if (challengeId && permissions.includes('idea')) {
      localStorage.removeItem("redirectChallengeId")
      navigate(`/hub/startup/challenge/${challengeId}`, { replace: true })
    } else {
      navigate(getStartRoute(permissions), { replace: true })
    }
  }

  const setUserPermissions = async (isCaixaEmployee: boolean = false): Promise<void> => {
    try {
      
      if (isCaixaEmployee) {
        const caixaPermissions = {
          id: '00000000-0000-0000-0000-000000000000',
          name: 'CaixaEmployee',
          features: [
            { 
              id: '00000000-0000-0000-0000-000000000001',
              code: 'caixa_employee',
              access: [
                { id: '00000000-0000-0000-0000-000000000001', code: 'view' },
                { id: '00000000-0000-0000-0000-000000000002', code: 'edit' },
                { id: '00000000-0000-0000-0000-000000000003', code: 'launch' }
              ]
            },
            { 
              id: '00000000-0000-0000-0000-000000000002',
              code: 'idea',
              access: [
                { id: '00000000-0000-0000-0000-000000000004', code: 'view' },
                { id: '00000000-0000-0000-0000-000000000005', code: 'edit' },
                { id: '00000000-0000-0000-0000-000000000006', code: 'launch' }
              ]
            },
            { 
              id: '00000000-0000-0000-0000-000000000003',
              code: 'evaluation',
              access: [
                { id: '00000000-0000-0000-0000-000000000007', code: 'view' },
                { id: '00000000-0000-0000-0000-000000000008', code: 'edit' },
                { id: '00000000-0000-0000-0000-000000000009', code: 'launch' }
              ]
            }
          ]
        }
        
        dispatch(setPermissions(caixaPermissions))
        const permissions = ['caixa_employee', 'idea', 'evaluation']
        
        redirectAfterLogin(permissions)
        return
      }
      
      const response = await api.get('/v1/system/user/my-permissions')
      
      if (response.status === 200) {
        let permissionsData = response?.data?.data
        
        dispatch(setPermissions(permissionsData))
        const permissions: string[] = []
        permissionsData.features?.map((feature, index): any => {
          permissions.push(String(feature.code))
          return feature
        })
        
        redirectAfterLogin(permissions)
      } else {
        throw new Error(`Status inválido: ${response.status}`)
      }
    } catch (error) {
      if (isCaixaEmployee) {
        const mockPermissions = {
          id: '00000000-0000-0000-0000-000000000000',
          name: 'CaixaEmployee',
          features: [
            { 
              id: '00000000-0000-0000-0000-000000000001',
              code: 'caixa_employee',
              access: [
                { id: '00000000-0000-0000-0000-000000000001', code: 'view' },
                { id: '00000000-0000-0000-0000-000000000002', code: 'edit' },
                { id: '00000000-0000-0000-0000-000000000003', code: 'launch' }
              ]
            },
            { 
              id: '00000000-0000-0000-0000-000000000002',
              code: 'idea',
              access: [
                { id: '00000000-0000-0000-0000-000000000004', code: 'view' },
                { id: '00000000-0000-0000-0000-000000000005', code: 'edit' },
                { id: '00000000-0000-0000-0000-000000000006', code: 'launch' }
              ]
            },
            { 
              id: '00000000-0000-0000-0000-000000000003',
              code: 'evaluation',
              access: [
                { id: '00000000-0000-0000-0000-000000000007', code: 'view' },
                { id: '00000000-0000-0000-0000-000000000008', code: 'edit' },
                { id: '00000000-0000-0000-0000-000000000009', code: 'launch' }
              ]
            }
          ]
        }
        dispatch(setPermissions(mockPermissions))
        navigate('/hub/startup/challenge', { replace: true })
      } else {
        showToast('error', 'Erro ao carregar permissões. Tente fazer login novamente.')
        console.error('Erro fatal: não foi possível carregar permissões para usuário não-Caixa')

      }
    }
  }

  const setUserProfileImage = async (userId: string, isCaixaEmployee: boolean = false): Promise<void> => {
    try {
      const noImage = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460__340.png'
      
      const endpoint = isCaixaEmployee ? `/v1/system/caixa-employee` : `/v1/system/user/${userId}`
      
      
      const response = await api.get(endpoint)
      if (response.status === 200) {
        
        let profileImageLink = noImage
        
        if (isCaixaEmployee) {
          profileImageLink = response.data.data?.profileImage || 
                           response.data.data?.avatar || 
                           response.data.data?.profileImageUrl || 
                           noImage
          
          const userData = response.data.data
          if (userData) {            
            const completeUserData = {
              id: userId,
              data: {
                ...userData,
                token: store.getState().auth.data?.token, // Manter o token atual
                temporaryToken: store.getState().auth.data?.temporaryToken, // Manter o temporaryToken
                name: userData.name || userData.userName || userData.fullName || 'Usuário',
                email: userData.email || userData.userEmail || '',
                role: {
                  id: userData.roleId || '00000000-0000-0000-0000-000000000000',
                  name: userData.role || 'CaixaEmployee'
                },
                company: {
                  id: userData.companyId || '00000000-0000-0000-0000-000000000000',
                  name: userData.companyName || 'Caixa Econômica Federal'
                }
              },
              isAuthenticated: true
            }
            
            store.dispatch(setAuth(completeUserData))
          }
        } else {
        profileImageLink = response.data.data?.profileImage || noImage
        }
        
        store.dispatch(updateProfileImageLink(profileImageLink))
      } else {
        store.dispatch(updateProfileImageLink(noImage))
      }
    } catch (error) {
      const noImage = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460__340.png'
      store.dispatch(updateProfileImageLink(noImage))
    }
  }

  const setUpLogin = async (data) => {
    try {

      
      if (!data || !data.data || !data.data.token) {
        console.error('Dados de login inválidos:', data)
        showToast('error', 'Erro interno: dados de autenticação inválidos')
        return
      }


      let isCaixaEmployee = false
      try {
        const tokenPayload = JSON.parse(atob(data.data.token.split('.')[1]))
        isCaixaEmployee = tokenPayload.role === 'CaixaEmployee'
      } catch (error) {
        isCaixaEmployee = data.data.email?.includes('@caixa') ?? false
      }


      dispatch(setAuth(data))
      
      if (data.id) {
        
        if (isCaixaEmployee) {
          setUserProfileImage(data.id, isCaixaEmployee).then(() => {
            setUserPermissions(isCaixaEmployee)
          })
        } else {
          setUserProfileImage(data.id, isCaixaEmployee)
          setUserPermissions(isCaixaEmployee)
        }
      } else {
        setUserPermissions(isCaixaEmployee)
      }
    } catch (error) {
      console.error('Erro ao configurar login:', error)
      showToast('error', 'Erro interno ao configurar login')
    }
  }

  const handleSubmit = useCallback((formData: ExtendedLoginFields) => {
    setIsLoading(true)
    const request: PostFormat = {
      url: '/v1/auth/login',
      data:  { data: formData },
      httpConfig: { params: { isPublic: true } }
    }

    mutate(request, {
      onSuccess: (response) => {
        const data = response.data
        formData.Authorization = data.data.temporaryToken
        if (data.data.token) {
          setUpLogin(data)
        }
        else {
          setIsTwoFactorEnabled(true)
        }
        setIsLoading(false)
      },
      onError: (error) => {
        setIsLoading(false)
        showToast(
          'error',
          error?.response?.data?.message ??
            trans('Não foi possível enviar o código de verificação. Tente novamente mais tarde.')
        )
      }
    })
  }, [])

  const handleTwoFactor = (formData: ExtendedLoginFields) => {
    setIsLoading(true)
    const request: PostFormat = {
      url: '/v1/auth/2fa',
      data: { data: { Code2Fa : formData.twoFactorCode } },
       httpConfig: {
        headers: { 
          Authorization: `Bearer ${formData.Authorization}` 
        }
      }
    }
    mutate(request, {
      onSuccess: (response) => {
        if (response.data && response.data.token) {
            const data = {
              id: response.data.id || response.data.userId,
              data: {
                token: response.data.token,
                temporaryToken: formData.Authorization,
                ...response.data.data,
                name: response.data.data?.name || response.data.data?.userName || response.data.data?.fullName || 'Usuário',
                email: response.data.data?.email || response.data.data?.userEmail || '',
                role: {
                  id: response.data.data?.roleId || '00000000-0000-0000-0000-000000000000',
                  name: response.data.data?.role || 'CaixaEmployee'
                },
                company: {
                  id: response.data.data?.companyId || '00000000-0000-0000-0000-000000000000',
                  name: response.data.data?.companyName || 'Caixa Econômica Federal'
                }
              }
            }
          
          setUpLogin(data)
        }
        else {
          console.error('Resposta 2FA inválida:', response)
          showToast('error', trans('Código informado é inválido'))
          setIsTwoFactorEnabled(false)
        }
        setIsLoading(false)
      },
      onError: (error) => {
        showToast('error', error.response.data.message)
        setIsTwoFactorEnabled(false)
        setIsLoading(false)
      }
    })
  }

  return (
    <EspacoTeiaAuthPageLayout>
      <TeiaLoginForm
        formTitle={trans('Acesse sua conta')}
        requireFormLabel={trans('Digite seu email e senha')}
        userFormLabel={trans('Email')}
        passwordFormLabel={trans('Senha')}
        onSubmit={handleSubmit}
        twoFactorEnabled={isTwoFactorEnabled}
        isLoading={isLoading}
        onClickTwoFactorConfirmation={handleTwoFactor}
        {...({
          createResolverLabel: trans('Ainda não é membro?'),
          createResolverPath: `${LANDING_PAGE_URL}/contato`
        } as any)}
      />
    </EspacoTeiaAuthPageLayout>
  )
}
